import { applyPatches } from 'immer'
import { createUUID } from '../../utils'
import { JBAction } from '../../../../../../../store/state'

/** @typedef {{ id: string, action: UnknownAction, undoPatch: Patch[], redoPatch: Patch[] }} HistoryPatch
 * */

/**
 * Class representing a history reducer for managing undo and redo actions.
 */
export class HistoryReducer {
  /** @type {HistoryPatch[]} */
  #undoStack = []
  /** @type {HistoryPatch[]} */
  #redoStack = []

  /** @type {JBAction[]} */
  #enabledActions = [
    // node actions
    JBAction.AUTO_LAYOUT_WORKFLOW,
    JBAction.NODES_MOVED,
    JBAction.NODE_DROPPED_FROM_PALETTE,
    JBAction.NODE_LABEL_CHANGED,
    JBAction.NODE_DATA_CHANGED,
    JBAction.NODE_NEXT_CONNECTION_CREATED,
    JBAction.NODE_COPIED,
    JBAction.NODE_LINKS_REMOVED,
    JBAction.NODE_REMOVED,
    // link actions
    JBAction.LINK_CREATED,
    JBAction.LINK_TARGET_REPLACED,
    JBAction.LINK_LABEL_CHANGED,
    JBAction.LINK_REMOVED,
    // clipboard actions
    JBAction.CUT_SELECTION,
    JBAction.REMOVE_SELECTION,
    JBAction.PASTE
  ]

  /**
   * Undo the last action.
   * @param {JBState} currentState - The current state before undo.
   * @returns {JBState} The new state after undo.
   */
  undo(currentState) {
    if (!this.#hasUndo()) {
      console.warn('Cannot perform undo because there is nothing left to undo')
      return currentState
    }
    const lastPatch = this.#undoStack.shift()
    this.#redoStack.unshift(lastPatch)

    try {
      return applyPatches(currentState, lastPatch.undoPatch)
    } catch (error) {
      console.warn('Failed to apply undo patch:', error, {
        currentState,
        undoPatch: lastPatch.undoPatch
      })
      return currentState // Return the state unchanged if the patch fails
    }
  }

  /**
   * Redo the last undone action.
   * @param {JBState} currentState - The current state before redo.
   * @returns {JBState} The new state after redo.
   */
  redo(currentState) {
    if (!this.#hasRedo()) {
      console.warn('Cannot perform redo because there is nothing left to redo')
      return currentState
    }
    const nextPatch = this.#redoStack.shift()
    this.#undoStack.unshift(nextPatch)

    try {
      return applyPatches(currentState, nextPatch.redoPatch)
    } catch (error) {
      console.warn('Failed to apply redo patch:', error, {
        currentState,
        redoPatch: nextPatch.redoPatch
      })
      return currentState // Return the state unchanged if the patch fails
    }
  }

  /**
   * Store an action with its corresponding undo and redo patches.
   * @param {UnknownAction} action - The action to store.
   * @param {Patch[]} undoPatch - The patch to undo the action.
   * @param {Patch[]} redoPatch - The patch to redo the action.
   */
  storeAction(action, undoPatch, redoPatch) {
    if (!this.#enabledActions.includes(action.type)) {
      return
    }
    this.#undoStack.unshift({
      id: createUUID(),
      action,
      undoPatch,
      redoPatch
    })
    this.#redoStack = []
  }

  /**
   * Get the current state of the history.
   * @returns {HistoryState} The current state of the history.
   */
  getState() {
    return {
      hasUndo: this.#hasUndo(),
      hasRedo: this.#hasRedo(),
      lastPatchId: this.#undoStack[0]?.id || ''
    }
  }

  /**
   * Reset the history by clearing the undo and redo stacks.
   */
  reset() {
    this.#undoStack = []
    this.#redoStack = []
  }

  /**
   * Check if there are actions to undo.
   * @returns {boolean} True if there are actions to undo, false otherwise.
   * @private
   */
  #hasUndo() {
    return this.#undoStack.length > 0
  }

  /**
   * Check if there are actions to redo.
   * @returns {boolean} True if there are actions to redo, false otherwise.
   * @private
   */
  #hasRedo() {
    return this.#redoStack.length > 0
  }
}
